import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface NMXa073FormData {
  // Información general
  noSolicitud: string
  numeroSolicitud: string
  fecha: string
  hora: string
  grados: string
  hr: string
  idTermohigrometro: string
  realizo: string
  
  // Resultados SECO
  secoUrdimbreTransferencia: string
  secoUrdimbreCambioColor: string
  secoTramaTransferencia: string
  secoTramaCambioColor: string
  
  // Resultados HÚMEDO
  humedoUrdimbreTransferencia: string
  humedoUrdimbreCambioColor: string
  humedoTramaTransferencia: string
  humedoTramaCambioColor: string
  
  // Supervisión y observaciones
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean

  // Control de reporte al cliente
  reportarCambioColor: boolean
}

interface NMXa073FormProps {
  mode: "create" | "view" | "edit"
  initialData?: Partial<NMXa073FormData>
  onSave?: (data: NMXa073FormData) => void
  onCancel?: () => void
  rol?: string // <-- NUEVO: rol del usuario
  realizoUser?: string // <-- NUEVO: usuario que realizó la prueba
  onRefreshBackend?: () => void
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXa073Form({ mode, initialData = {}, onSave, onCancel, rol, realizoUser, onRefreshBackend, pedidoRowId, onAutosave }: NMXa073FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;

  const safeRealizoUser = typeof realizoUser === 'string' ? realizoUser : '';
  const [formData, setFormData] = useState<NMXa073FormData>({
    noSolicitud: "",
    numeroSolicitud: "",
    fecha: "",
    hora: "",
    grados: "",
    hr: "",
    idTermohigrometro: "",
    realizo: (mode === 'create' && realizoUser) ? realizoUser : "",
    secoUrdimbreTransferencia: "",
    secoUrdimbreCambioColor: "",
    secoTramaTransferencia: "",
    secoTramaCambioColor: "",
    humedoUrdimbreTransferencia: "",
    humedoUrdimbreCambioColor: "",
    humedoTramaTransferencia: "",
    humedoTramaCambioColor: "",
    superviso: "",
    observaciones: "",
    reportarCambioColor: false,
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  // --- Utilidad para obtener el nombre del supervisor desde initialData ---
  const getSupervisorName = () => {
    // Prioridad: initialData.superviso, realizoUser, globalRol, formData.superviso
    return (
      (initialData as any).SUPERVISO ||
      initialData.superviso ||
      realizoUser ||
      (typeof globalRol === 'string' ? globalRol : '') ||
      formData.superviso ||
      ''
    );
  };

  // --- Utilidad para obtener el nombre del técnico que realizó desde initialData ---
  const getRealizoName = () => {
    // Prioridad: initialData.realizo, realizoUser, globalRol, formData.realizo
    return (
      (initialData as any).REALIZO ||
      initialData.realizo ||
      realizoUser ||
      (typeof globalRol === 'string' ? globalRol : '') ||
      formData.realizo ||
      ''
    );
  };

  useEffect(() => {
    // Solo asignar realizo en modo create si no está ya en el estado
    if (mode === 'create' && realizoUser && !formData.realizo) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [realizoUser, mode, formData.realizo])

  // El campo superviso nunca se asigna en el estado antes de la acción

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData(prev => ({ ...prev, ...initialData }))
    setEditMode(false)
  }, [initialData])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  // --- Lógica robusta para saber si la prueba está aceptada o rechazada ---
  // Igual que AnalisisQuimicoForm: soporta array y objeto para initialData
  const initialDataObj = Array.isArray(initialData) ? initialData[0] : initialData || {};
  function getEstatusFromInitialData(data: any): string {
    if (!data) return '';
    // Busca en todas las variantes posibles
    const keys = Object.keys(data);
    for (const k of keys) {
      if (k.toLowerCase() === 'aceptado' || k.toLowerCase() === 'aceptar') {
        const val = data[k];
        if (typeof val === 'string') return val.toLowerCase().trim();
      }
    }
    return '';
  }
  const aceptadoValue = getEstatusFromInitialData(initialDataObj);
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  // --- ADMIN ACTIONS robustas igual que AnalisisQuimicoForm ---
  // RowId robusto: primero formData, luego initialDataObj, luego vacío
  const [autosaveRowId, setAutosaveRowId] = useState<string>((formData as any)?.ROW_ID || (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialDataObj as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = (formData as any).PEDIDO || (initialDataObj as any)?.PEDIDO || '';
  const pruebaValue = (formData as any).PRUEBA || (initialDataObj as any)?.PRUEBA || '';

  const handleInputChange = (field: keyof NMXa073FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleReportarCambioColorChange = (value: boolean) => {
    setFormData(prev => ({ ...prev, reportarCambioColor: value }))
  }

  const formPayload = () => ({ ...formData })

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formPayload())
      }
    } finally {
      setGuardando(false)
    }
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-073-INNTEX-2005 SOLIDEZ DEL COLOR AL FROTE',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async (
    accion: 'aceptado' | 'rechazado',
    razonRechazo?: string
  ) => {
    if (accion === 'rechazado' && !razonRechazo) {
      setShowRechazoModal(true);
      return;
    }
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-073?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-073?';
    if (!window.confirm(confirmMsg)) return;
    setRechazoLoading(true);
    const payload = {
      ...formPayload(),
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' && razonRechazo ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
      setShowRechazoModal(false);
    }
  };

  const handleEditar = () => setEditMode(true);

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-073? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formPayload(),
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "Crear NMX-A-073-INNTEX-2005"
      case "edit":
        return "Editar NMX-A-073-INNTEX-2005"
      case "view":
        return "Ver NMX-A-073-INNTEX-2005"
      default:
        return "NMX-A-073-INNTEX-2005"
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        {/* Elimino el botón Guardar aquí para evitar duplicidad en modo create */}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-center text-lg">
            NMX-A-073-INNTEX-2005 SOLIDEZ DEL COLOR AL FROTE
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Información General */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <tbody>
              {/* Primera fila - Información básica */}
              <tr>
                <td className="border border-border p-2 bg-muted font-medium whitespace-nowrap">
                  NO. DE SOLICITUD:
                </td>
                <td className="border border-border p-1">
                  <Input
                    type="text"
                    placeholder="Número de solicitud"
                    value={formData.noSolicitud}
                    onChange={(e) => handleInputChange('noSolicitud', e.target.value)}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
                <td className="border border-border p-1 text-center">
                 
                </td>
                <td className="border border-border p-2 bg-muted font-medium">
                  FECHA:
                </td>
                <td className="border border-border p-1">
                  <Input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => handleInputChange('fecha', e.target.value)}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
                <td className="border border-border p-1 text-center">
                  
                </td>
              </tr>

              {/* Segunda fila - Condiciones ambientales */}
              <tr>
                <td className="border border-border p-2 bg-muted font-medium">
                  HORA:
                </td>
                <td className="border border-border p-1">
                  <Input
                    type="time"
                    value={formData.hora}
                    onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
                <td className="border border-border p-2 bg-muted font-medium">
                  ° C:
                </td>
                <td className="border border-border p-1">
                  <Input
                    type="number"
                    placeholder="Temperatura"
                    value={formData.grados}
                    onChange={(e) => handleInputChange('grados', e.target.value)}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
                <td className="border border-border p-2 bg-muted font-medium">
                  % HR:
                </td>
                <td className="border border-border p-1">
                  <Input
                    type="number"
                    placeholder="Humedad relativa"
                    value={formData.hr}
                    onChange={(e) => handleInputChange('hr', e.target.value)}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
              </tr>

              {/* Tercera fila - Equipo y responsable */}
              <tr>
                <td className="border border-border p-2 bg-muted font-medium" colSpan={4}>
                  ID DE TERMOHIGRÓMETRO:
                </td>
                <td className="border border-border p-1" colSpan={2}>
                  <Input
                    type="text"
                    placeholder="ID del termohigrómetro"
                    value={formData.idTermohigrometro}
                    onChange={(e) => handleInputChange('idTermohigrometro', e.target.value)}
                    disabled={isReadOnly}
                    className="border-0 bg-transparent"
                  />
                </td>
              </tr>

              <tr>
                <td className="border border-border p-2 bg-muted font-medium" colSpan={4}>
                  REALIZÓ:
                </td>
                <td className="border border-border p-1" colSpan={2}>
                  <Input
                    type="text"
                    placeholder="Nombre del responsable"
                    value={formData.realizo}
                    onChange={(e) => handleInputChange('realizo', e.target.value)}
                    disabled
                    className="border-0 bg-transparent"
                  />
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          {/* Tabla de Resultados */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Switch
                id="reportarCambioColor"
                checked={formData.reportarCambioColor}
                onCheckedChange={(checked) => handleReportarCambioColorChange(checked === true)}
                disabled={isReadOnly}
              />
              <Label htmlFor="reportarCambioColor" className="text-sm leading-tight">
                Reportar cambio de color al cliente
              </Label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border">
                <tbody>
                {/* Encabezados principales */}
                <tr>
                  <td className="border border-border p-3 bg-muted"></td>
                  <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                    SECO
                  </td>
                  <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                    HÚMEDO
                  </td>
                  <td className="border border-border p-3 bg-muted"></td>
                </tr>

                {/* Subencabezados */}
                <tr>
                  <td className="border border-border p-2 bg-muted"></td>
                  <td className="border border-border p-2 bg-muted text-center font-medium">
                    GRADO DE TRANSFERENCIA
                  </td>
                  <td className="border border-border p-2 bg-muted text-center font-medium">
                    GRADO DE CAMBIO DE COLOR
                  </td>
                  <td className="border border-border p-2 bg-muted text-center font-medium">
                    GRADO DE TRANSFERENCIA
                  </td>
                  <td className="border border-border p-2 bg-muted text-center font-medium">
                    GRADO DE CAMBIO DE COLOR
                  </td>
                  <td className="border border-border p-2 bg-muted"></td>
                </tr>

                {/* Fila URDIMBRE */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    URDIMBRE
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.secoUrdimbreTransferencia}
                      onChange={(e) => handleInputChange('secoUrdimbreTransferencia', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.secoUrdimbreCambioColor}
                      onChange={(e) => handleInputChange('secoUrdimbreCambioColor', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.humedoUrdimbreTransferencia}
                      onChange={(e) => handleInputChange('humedoUrdimbreTransferencia', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.humedoUrdimbreCambioColor}
                      onChange={(e) => handleInputChange('humedoUrdimbreCambioColor', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium" rowSpan={2}>
                    <div className="space-y-2">
                      <div className="text-sm">SUPERVISÓ:</div>
                      <Input
                        type="text"
                        placeholder="Nombre del supervisor"
                        value={formData.superviso}
                        disabled={true}
                        className="border-0 bg-transparent text-center"
                      />
                    </div>
                  </td>
                </tr>

                {/* Fila TRAMA */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    TRAMA
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.secoTramaTransferencia}
                      onChange={(e) => handleInputChange('secoTramaTransferencia', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.secoTramaCambioColor}
                      onChange={(e) => handleInputChange('secoTramaCambioColor', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.humedoTramaTransferencia}
                      onChange={(e) => handleInputChange('humedoTramaTransferencia', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder=""
                      value={formData.humedoTramaCambioColor}
                      onChange={(e) => handleInputChange('humedoTramaCambioColor', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent text-center"
                    />
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <div className="border border-border p-2 bg-muted font-medium flex items-center gap-2">
              OBSERVACIONES:
              <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </div>
            <Textarea
              placeholder="Observaciones adicionales sobre la prueba..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              disabled={isReadOnly}
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botones de acciones en modo view y no aceptado/rechazado SOLO PARA ADMIN */}
        {mode === 'view' && !editMode && puedeRevisar && !isAceptado && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
            <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar}>Editar</Button>
          </>
        )}
        {/* Botón guardar edición SOLO PARA ADMIN */}
        {mode === 'view' && editMode && puedeRevisar && !isAceptado && (
          <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
        )}
        {/* Botón guardar en modo create */}
        {mode === 'create' && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>
      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}